import { useState, useEffect } from "react";
import { TextField, Button } from "@mui/material";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { loginUser, registrationUser } from "../redux/asyncThunk/userActions";
import { useDispatch, useSelector } from "react-redux";
import { userSlice } from "../redux/user/userSlice";
import AuthLayout, { AUTH_FIELD_SX } from "./AuthLayout";
import PasswordField from "../components/PasswordField";
import {
  REQUIRED_FIELDS_MESSAGE,
  validateLoginForm,
  validateRegForm,
  sanitizeEmailInput,
  sanitizePasswordInput,
} from "../utils/authForms";
import "./index.css";

const { clearMessages } = userSlice;

export default function Login() {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.data);
  const messages = useSelector((state) => state.user.messages);

  const [tab, setTab] = useState("login");

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [regData, setRegData] = useState({
    regLogin: "",
    regPassword: "",
    confirmRegPassword: "",
  });
  const [loginErrors, setLoginErrors] = useState({});
  const [regErrors, setRegErrors] = useState({});
  const [message, setMessage] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      navigate("/main");
    }
  }, [navigate, user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setTab(params.get("tab") === "register" ? "register" : "login");
  }, [location.search]);

  useEffect(() => {
    setLoginData({ email: "", password: "" });
    setRegData({
      regLogin: "",
      regPassword: "",
      confirmRegPassword: "",
    });
    setLoginErrors({});
    setRegErrors({});
    setMessage({ type: "", message: "" });
    dispatch(clearMessages());
  }, [dispatch, location.pathname, tab]);

  useEffect(() => {
    const latest = messages[messages.length - 1];
    if (latest?.message) {
      setMessage(latest);
    }
  }, [messages]);

  const switchTab = (nextTab) => {
    setMessage({ type: "", message: "" });
    setLoginErrors({});
    setRegErrors({});
    setTab(nextTab);
    navigate(nextTab === "register" ? "/login?tab=register" : "/login", {
      replace: true,
    });
  };

  const changeLoginData = (event) => {
    const { id, value } = event.target;
    const sanitized =
      id === "email" ? sanitizeEmailInput(value) : sanitizePasswordInput(value);

    setMessage({ type: "", message: "" });
    setLoginErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setLoginData((prev) => ({
      ...prev,
      [id]: sanitized,
    }));
  };

  const changeRegData = (event) => {
    const { id, value } = event.target;
    const sanitized =
      id === "regLogin"
        ? sanitizeEmailInput(value)
        : sanitizePasswordInput(value);

    setMessage({ type: "", message: "" });
    setRegErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      if (id === "regPassword" || id === "confirmRegPassword") {
        delete next.regPassword;
        delete next.confirmRegPassword;
      }
      return next;
    });
    setRegData((prev) => ({
      ...prev,
      [id]: sanitized,
    }));
  };

  const handleLoginButton = async (event) => {
    event?.preventDefault();
    const errors = validateLoginForm(loginData);

    if (Object.keys(errors).length > 0) {
      setLoginErrors(errors);
      setMessage({ type: "error", message: REQUIRED_FIELDS_MESSAGE });
      return;
    }

    setLoginErrors({});
    setMessage({ type: "", message: "" });
    setIsSubmitting(true);
    try {
      await dispatch(
        loginUser({ login: loginData.email, password: loginData.password })
      ).unwrap();
    } catch (err) {
      setMessage({
        type: "error",
        message: err?.message || "Не удалось выполнить вход. Попробуйте позже.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegButton = async (event) => {
    event?.preventDefault();
    const errors = validateRegForm(regData);

    if (Object.keys(errors).length > 0) {
      setRegErrors(errors);
      const hasEmpty =
        !regData.regLogin.trim() ||
        !regData.regPassword ||
        !regData.confirmRegPassword;
      setMessage({
        type: "error",
        message: hasEmpty
          ? REQUIRED_FIELDS_MESSAGE
          : Object.values(errors)[0],
      });
      return;
    }

    setRegErrors({});
    setMessage({ type: "", message: "" });
    setIsSubmitting(true);
    try {
      await dispatch(
        registrationUser({
          login: regData.regLogin,
          password: regData.regPassword,
        })
      ).unwrap();
    } catch (err) {
      setMessage({
        type: "error",
        message:
          err?.message || "Не удалось завершить регистрацию. Попробуйте позже.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title={tab === "login" ? "Добро пожаловать" : "Создайте аккаунт"}
      subtitle={
        tab === "login"
          ? "Войдите, чтобы управлять своими курсами"
          : "Зарегистрируйтесь и соберите первый сценарий"
      }
    >
      <div className="auth-tabs">
        <button
          type="button"
          className={`auth-tabs__btn${tab === "login" ? " auth-tabs__btn--active" : ""}`}
          onClick={() => switchTab("login")}
        >
          Вход
        </button>
        <button
          type="button"
          className={`auth-tabs__btn${tab === "register" ? " auth-tabs__btn--active" : ""}`}
          onClick={() => switchTab("register")}
        >
          Регистрация
        </button>
      </div>

      {tab === "login" ? (
        <form onSubmit={handleLoginButton} noValidate>
          <div className="auth-card__body">
            <TextField
              id="email"
              label="Email"
              variant="outlined"
              type="email"
              required
              autoComplete="email"
              sx={AUTH_FIELD_SX}
              value={loginData.email}
              onChange={changeLoginData}
              error={Boolean(loginErrors.email)}
              helperText={loginErrors.email}
            />
            <PasswordField
              id="password"
              label="Пароль"
              variant="outlined"
              required
              autoComplete="current-password"
              sx={AUTH_FIELD_SX}
              value={loginData.password}
              onChange={changeLoginData}
              error={Boolean(loginErrors.password)}
              helperText={loginErrors.password}
            />
            <div className="auth-form__link-row">
              <Link to="/reset" className="auth-form__link">
                Забыли пароль?
              </Link>
            </div>
            {message?.message ? (
              <div
                className={`auth-form__message auth-form__message--${
                  message.type === "error" ? "error" : "success"
                }`}
                role="alert"
              >
                {message.message}
              </div>
            ) : null}
            <Button
              type="submit"
              variant="contained"
              className="auth-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Вход…" : "Войти"}
            </Button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleRegButton} noValidate>
          <div className="auth-card__body">
            <TextField
              id="regLogin"
              label="Email"
              variant="outlined"
              type="email"
              required
              autoComplete="email"
              value={regData.regLogin}
              sx={AUTH_FIELD_SX}
              onChange={changeRegData}
              error={Boolean(regErrors.regLogin)}
              helperText={regErrors.regLogin}
            />
            <PasswordField
              id="regPassword"
              label="Пароль"
              variant="outlined"
              required
              autoComplete="new-password"
              value={regData.regPassword}
              sx={AUTH_FIELD_SX}
              onChange={changeRegData}
              error={Boolean(regErrors.regPassword)}
              helperText={regErrors.regPassword}
            />
            <PasswordField
              id="confirmRegPassword"
              label="Повторите пароль"
              variant="outlined"
              required
              autoComplete="new-password"
              value={regData.confirmRegPassword}
              sx={AUTH_FIELD_SX}
              onChange={changeRegData}
              error={Boolean(regErrors.confirmRegPassword)}
              helperText={regErrors.confirmRegPassword}
            />
            {message?.message ? (
              <div
                className={`auth-form__message auth-form__message--${
                  message.type === "error" ? "error" : "success"
                }`}
                role="alert"
              >
                {message.message}
              </div>
            ) : null}
            <Button
              type="submit"
              variant="contained"
              className="auth-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Регистрация…" : "Зарегистрироваться"}
            </Button>
          </div>
        </form>
      )}

      <div className="auth-card__footer auth-card__footer--inline">
        {tab === "login" ? (
          <>
            Нет аккаунта?{" "}
            <button
              type="button"
              className="auth-link-btn"
              onClick={() => switchTab("register")}
            >
              Зарегистрироваться
            </button>
          </>
        ) : (
          <>
            Уже есть аккаунт?{" "}
            <button
              type="button"
              className="auth-link-btn"
              onClick={() => switchTab("login")}
            >
              Войти
            </button>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
